import { CHIEF_COMPLAINT_MAX_LENGTH } from '@/src/constants'
import { useAppSelector } from '@/src/store/hooks'
import { icdsAction } from '@api'
import { useEncounter, useIntake, useMedical, useUserRole } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { medicalSchema } from '@schema/pDirectory.schema'
import { MedDataMock } from '@type/history.type'
import { IcdDataMock } from '@type/icd.type'
import { FormButton, Modal, SelectInputField, TextAreaField } from '@ui'
import { Select } from '@ui/SearchInput/Select'
import { flattenKeysAndValuesUnique, getError, hasAccessToUpdateManage, showToast, showWarningToast } from '@util'
import { Box, Form, Text } from 'grommet'
import { useEffect, useState } from 'react'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import { useParams, useSearchParams } from 'react-router-dom'

type Props = {
  onClose: () => void
  id?: string
  defaultData?: MedDataMock
}

const EditMedical = ({ id, defaultData, onClose }: Props) => {
  const [onChangeOptions, setOnChangeOptions] = useState<IcdDataMock[]>()
  const { medical, updateMedical } = useMedical({ id })
  const status = useAppSelector(state => state.config.medical_history_transitions as Record<string, string[]>)
  const codesArray = medical?.data.relationships.icd_codes.data?.map(({ id }) => id)
  const icdsData: any = medical?.included.filter(({ type, id }) => type === 'icd_code' && codesArray?.includes(id))
  const methods = useForm<any>({
    resolver: yupResolver(medicalSchema),
    defaultValues: { ...defaultData?.attributes, status: defaultData?.meta.status }
  })
  const [searchParams] = useSearchParams()
  const { patientId, encounterId, intakeId: paramsIntakeId } = useParams<string>()

  const isEncounterProblemTab = searchParams.get('encounterTab') === 'Encounter Problems'

  const { singleEncounter } = useEncounter({ id: encounterId })
  const intakeId = singleEncounter?.relationships.intake.data.id

  const { intake, updateIntake } = useIntake({ id: intakeId })
  const { isSuperAdmin } = useUserRole()
  const permissions = useAppSelector(state => state.user.user?.permissions)
  // Copying the text onto the intake is best-effort, and only for roles allowed to edit intakes
  // (IntakePolicy#update?); otherwise the PATCH would 403 after the problem itself saved.
  const canEditIntake = hasAccessToUpdateManage(permissions, 'intake', isSuperAdmin)

  const chiefComplaint = intake?.data.attributes.chief_complaint

  useEffect(() => {
    if (isEncounterProblemTab && chiefComplaint && !methods.getValues('comments')) {
      methods.setValue('comments', chiefComplaint)
    }
  }, [chiefComplaint, isEncounterProblemTab])

  const commentValue: string = useWatch({ control: methods.control, name: 'comments' }) || ''

  const onSubmit = async (data: any) => {
    const payload = {
      data: {
        attributes: {
          ...data
        },
        type: 'medical_history'
      }
    }
    // The problem save alone decides success; the modal closes once it has saved.
    try {
      await updateMedical({ formVal: payload, id: id || '' })
    } catch (error) {
      getError(error)
      return
    }

    // Keep the Intake's Chief Complaint in sync with the Clinical Presentation text (Encounter
    // Problem flow only, and only if the text changed). A failure here must not undo the save.
    if (canEditIntake && isEncounterProblemTab && intakeId && data.comments && data.comments !== chiefComplaint) {
      try {
        await updateIntake({
          formVal: {
            data: {
              attributes: {
                chief_complaint: data.comments
              },
              id: intakeId,
              type: 'intake'
            }
          },
          id: intakeId
        })
      } catch {
        showWarningToast('Problem saved. The chief complaint on the intake could not be updated.')
        onClose()
        return
      }
    }

    showToast('Medical updated successfully')
    onClose()
  }
  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} title={isEncounterProblemTab ? 'Edit Encounter Problem' : 'Edit Medical'}>
        <Form
          onSubmit={methods.handleSubmit(onSubmit)}
          style={{ display: 'flex', flexDirection: 'column', overflow: 'auto' }}
        >
          <Box
            // flex
            overflow={'auto'}
            pad="medium"
            style={{ display: 'block', scrollbarColor: '#fff', scrollbarWidth: 'thin', width: '50 em' }}
          >
            <Box gap={'24px'}>
              <Box>
                <TextAreaField
                  label={isEncounterProblemTab ? 'Clinical Presentation (Chief Complaint)' : 'Comments'}
                  maxLength={CHIEF_COMPLAINT_MAX_LENGTH}
                  name="comments"
                  placeholder="Enter Note"
                  style={{ borderRadius: '14px', minHeight: '100px' }}
                />
                {isEncounterProblemTab && (
                  <Box direction="row" justify="between" margin={{ top: 'xsmall' }}>
                    <Text color="dark-4" size="xsmall">
                      Pre-filled from Chief Complaint. Edit as needed for this problem.
                    </Text>
                    <Text color="dark-4" size="xsmall">
                      {commentValue.length}/{CHIEF_COMPLAINT_MAX_LENGTH}
                    </Text>
                  </Box>
                )}
              </Box>

              <Box>
                <Controller
                  name="icd_codings_attributes"
                  render={({ field: { onChange }, fieldState: { error } }) => (
                    <Select
                      cacheOptions
                      defaultOptions
                      error={error?.message}
                      getOptionLabel={({ attributes }) => `${attributes.title} - ${attributes.code}`}
                      getOptionValue={({ id }) => id}
                      isAsync
                      isMulti
                      label={isEncounterProblemTab ? 'Coding (Diagnosis / Problem Code)' : 'Coding'}
                      loadOptions={icdsAction}
                      onChange={selectedOptions => {
                        const newVal = selectedOptions
                        setOnChangeOptions([...newVal])
                        const values = selectedOptions
                          ? selectedOptions?.map((option: any) => ({
                              icd_code_id: option?.id // Map the selected value
                            }))
                          : []

                        onChange(values) // Update the form value
                      }}
                      value={onChangeOptions || icdsData}
                    />
                  )}
                />
                {isEncounterProblemTab && (
                  <Text color="dark-4" margin={{ top: 'xsmall' }} size="xsmall">
                    Select the most appropriate code that matches the clinical presentation.
                  </Text>
                )}
              </Box>

              <Box>
                <SelectInputField label="Status" name="status" options={flattenKeysAndValuesUnique(status || {})} />
                {isEncounterProblemTab && (
                  <Text color="dark-4" margin={{ top: 'xsmall' }} size="xsmall">
                    Select the current status of this problem.
                  </Text>
                )}
              </Box>
            </Box>
          </Box>
          <FormButton isLoading={methods.formState.isSubmitting} onClose={onClose} submit="Save" />
        </Form>
      </Modal>
    </FormProvider>
  )
}

export default EditMedical
